import {FindQuery} from "./pj1dbApiWork";
import {Dict} from "./pj1dbApi";

// ====================================================================================================
// Common
// ====================================================================================================
export const FILE_TYPES = {
  SDF: "sdf",
} as const satisfies Record<string, string>;
export type FileType = typeof FILE_TYPES[keyof typeof FILE_TYPES];

export const WORLD_TYPES = {
  BASE: "base",
  CUSTOM: "custom",
} as const satisfies Record<string, string>;
export type WorldType = typeof WORLD_TYPES[keyof typeof WORLD_TYPES];

// ====================================================================================================
// World
// ====================================================================================================
export type WorldDocument = BaseWorldDocument | CustomWorldDocument;

// ----------------------------------------------------------------------------------------------------
// Find
// ----------------------------------------------------------------------------------------------------
export type FindWorldRequest = FindBaseWorldRequest | FindCustomWorldRequest;
export type FindWorldRequestByType<T extends WorldType | undefined | null> =
  T extends 'base' ? FindBaseWorldRequest :
    T extends 'custom' ? FindCustomWorldRequest :
      FindWorldRequest;

export type FindWorldResponse = FindBaseWorldResponse | FindCustomWorldResponse;
export type FindWorldResponseByType<T extends WorldType | undefined | null> =
  T extends 'base' ? FindBaseWorldResponse :
    T extends 'custom' ? FindCustomWorldResponse :
      FindWorldResponse;

// ----------------------------------------------------------------------------------------------------
// Register
// ----------------------------------------------------------------------------------------------------
export interface RegisterWorldRequestBody {
  's3-uri': string;
  'world-type': WorldType;
  'name-jp': string;

}

export interface RegisterWorldRequestPathParams {
  version: 'object-user';
}

export interface RegisterWorldRequest {
  body: RegisterWorldRequestBody;
  pathParams: RegisterWorldRequestPathParams;
}

export type RegisterWorldResponse = void;

// ====================================================================================================
// Base World
// ====================================================================================================
export interface BaseWorldDocument {
  attr: {
    _id: string;
    'world-id': string;
    'file-type': FileType;
    source: string;
    'world-type': 'base';
    'name-jp': string;
    'name-en': string;
    when: number;
    who: string;
    tag: Dict<string>
    version: string;
    creator: string;
    deleted: boolean;
    inserted: number;
    calc: {
      _id: string;
      'world-id': string;
      sha256: Dict<string>;
    };
    processing: {
      _id: string;
      'world-id': string;
      'in-progress': boolean;
      status: string;
      s3Key: {
        usdz: string;
        glb: string;
        thumbnail: string;
      };
    };
  },
  url: {
    usdz: string;
    glb: string;
    thumbnail: string;
  }
}

// ----------------------------------------------------------------------------------------------------
// Find
// ----------------------------------------------------------------------------------------------------
export interface FindBaseWorldRequestBody {
  'query-initial'?: object;
  'query-final'?: object;
}

export type FindBaseWorldRequestQuery = FindQuery

export interface FindBaseWorldRequest {
  body?: FindBaseWorldRequestBody;
  query?: FindBaseWorldRequestQuery;
}

export type FindBaseWorldResponse = Dict<BaseWorldDocument>;

// ----------------------------------------------------------------------------------------------------
// Register
// ----------------------------------------------------------------------------------------------------
export interface RegisterBaseWorldRequestBody {
  's3-uri': string;
  objectIds: string[];
}

export interface RegisterBaseWorldRequestPathParams {
  version: 'object-user';
}

export interface RegisterBaseWorldRequest {
  body: RegisterBaseWorldRequestBody;
  pathParams: RegisterBaseWorldRequestPathParams;
}

export type RegisterBaseWorldResponse = string;

// ====================================================================================================
// Custom World
// ====================================================================================================
export interface CustomWorldDocument {
  attr: {
    _id: string;
    'world-id': string;
    'file-type': FileType;
    source: string;
    'world-type': 'custom';
    'name-jp': string;
    'name-en': string;
    when: number;
    who: string;
    tag: Dict<string>
    version: string;
    creator: string;
    deleted: boolean;
    inserted: number;
    calc: {
      _id: string;
      'world-id': string;
      sha256: Dict<string>;
    };
    processing: {
      _id: string;
      'world-id': string;
      'in-progress': boolean;
      status: string;
      s3Key: {
        usdz: string;
        glb: string;
        thumbnail: string;
      };
    };
  },
  url: {
    usdz: string;
    glb: string;
    thumbnail: string;
  }
}

// ----------------------------------------------------------------------------------------------------
// Find
// ----------------------------------------------------------------------------------------------------
export interface FindCustomWorldRequestBody {
  'query-initial'?: object;
  'query-final'?: object;
}

export type FindCustomWorldRequestQuery = FindQuery

export interface FindCustomWorldRequest {
  body?: FindCustomWorldRequestBody;
  query?: FindCustomWorldRequestQuery;
}

export type FindCustomWorldResponse = Dict<CustomWorldDocument>;

// ----------------------------------------------------------------------------------------------------
// Register
// ----------------------------------------------------------------------------------------------------
export interface RegisterCustomWorldRequestBody {
  worldId: string;
  modelIds: string[];
  's3-uri': string;
  num: number;
  optional?: Partial<{
    block_num_max: number;
    place_stack_num_random: boolean;
    gap_size_x: number
    gap_size_y: number
    margin_min_x: number
    margin_min_y: number
    fill_rate_range_min: number
    fill_rate_range_max: number
    work_pos_misalign_max_rate_x: number
    work_pos_misalign_max_rate_y: number
    abnormal_orien_work_exist_max_rate: number
    mixed_in_work_exist_max_rate: number
  }>
}

export interface RegisterCustomWorldRequest {
  body: RegisterCustomWorldRequestBody;
}

export type RegisterCustomWorldResponse = void;
